<template>
  <div
    class="w-full h-auto min-h-screen mt-3 justify-between bg-background space-y-9 rounded-t-[20px]"
    @click="closeMenu"
  >
    <div class="mx-8 my-6">
      <div v-if="loading" class="flex justify-center items-center py-20">
        <Loader />
      </div>

      <div v-else>
        <div class="hr-container mb-2">
          <div class="flex items-start justify-between">
            <div class="space-y-3 flex-1">
              <div class="flex items-center space-x-3">
                <p class="font-semibold text-foreground text-[20px]">
                  {{ learningGoal?.topic || "Learning Goal" }}
                </p>
                <div
                  v-if="isProcessing"
                  class="flex items-center space-x-2 bg-[#e0e7ff] px-3 py-1.5 rounded-[8px] border border-[#818cf8]"
                >
                  <Loader />
                  <span class="text-[12px] font-medium text-primary"
                    >Processing...</span
                  >
                </div>
              </div>
            </div>
            <LearningGoalActionsBar
              v-if="learningGoal"
              :goal="learningGoal"
              :projectId="projectId"
              @book-mentor="onBookMentor"
              @edit-goal="onEditGoal"
            />
          </div>
          <!-- Full-width transparent description below row -->
          <div
            class="mt-2 w-full min-w-0 bg-transparent border border-border rounded-[12px] p-3"
          >
            <div class="flex items-start gap-2">
              <p class="text-foreground text-[14px] flex-1 break-words">
                {{ learningGoal?.description || defaultDescription }}
              </p>
              <button
                type="button"
                class="h-6 w-6 flex-shrink-0 flex items-center justify-center hover:bg-gray-100 transition-colors"
                @click="toggleDescription"
                :aria-expanded="descriptionExpanded.toString()"
                aria-label="Toggle description"
              >
                <svg
                  :class="{ 'rotate-180': descriptionExpanded }"
                  class="transition-transform"
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="#6E6E6F"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Progress Overview Section -->
        <div class="mb-8">
          <div
            v-if="isProcessing"
            class="bg-muted/30 border border-border rounded-[12px] p-6 mb-6"
          >
            <div class="flex items-start space-x-4">
              <div class="flex-shrink-0 mt-1">
                <Loader />
              </div>
              <div class="flex-1">
                <h3 class="text-[16px] font-semibold text-foreground mb-2">
                  Learning Goal is Being Generated
                </h3>
                <p class="text-[14px] text-muted-foreground">
                  Your learning goal is currently being processed. The learning
                  steps and tasks will appear once the generation is complete.
                  This may take a few moments.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!isProcessing"
          class="bg-card border-2 border-border rounded-[12px] p-6 mb-8"
        >
          <h2 class="text-[16px] font-semibold text-foreground">
            {{ parentTaskInfo.title || "Loading..." }}
          </h2>
          <p class="text-[12px] text-[#667085] mb-6">
            {{
              parentTaskInfo.description ||
              "Learning Resources & Recommendations"
            }}
          </p>

          <div
            v-if="!loading && learningSteps.length === 0"
            class="text-center py-8"
          >
            <p class="text-muted-foreground text-[14px]">
              No learning steps found for this goal.
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div
              v-for="(step, index) in learningSteps"
              :key="step.task_id || index"
              class="relative bg-card border border-border rounded-[12px] p-6 transition-all duration-300 ease-in-out hover:shadow-md"
              :class="{
                'opacity-40': !isCardActive(index),
                'cursor-pointer': isCardActive(index),
                'cursor-not-allowed': !isCardActive(index),
              }"
              @click="handleCardClick(index, step)"
            >
              <!-- Locked Overlay for Inactive Cards -->
              <div
                v-if="!isCardActive(index)"
                class="absolute inset-0 bg-background bg-opacity-70 rounded-[12px] z-10 transition-opacity duration-300"
              ></div>

              <div class="flex items-start justify-between mb-4">
                <div class="flex items-start space-x-3 flex-1">
                  <div
                    class="mt-1 flex-shrink-0 w-[30px] h-[30px] rounded-[15px] border flex items-center justify-center bg-muted/30 border-border"
                  >
                    <svg
                      width="12"
                      height="15"
                      viewBox="0 0 12 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.25 0.5V3.3M6 0.5V3.3M8.75 0.5V3.3M3.25 6.1H7.375M3.25 8.9H8.75M3.25 11.7H6.6875M1.875 1.9H10.125C10.8844 1.9 11.5 2.5268 11.5 3.3V13.1C11.5 13.8732 10.8844 14.5 10.125 14.5H1.875C1.11561 14.5 0.5 13.8732 0.5 13.1V3.3C0.5 2.5268 1.11561 1.9 1.875 1.9Z"
                        stroke="#D0D5DD"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>

                  <div class="flex-1">
                    <h3
                      class="text-[16px] font-semibold text-foreground mb-1 cursor-pointer hover:text-primary transition-colors"
                      @click.stop="openSidePanel(step)"
                    >
                      {{ step.title }}
                    </h3>
                    <p
                      v-if="step.description"
                      class="text-[13px] text-muted-foreground mb-2"
                    >
                      {{ step.description }}
                    </p>
                    <div
                      class="flex items-center space-x-4 text-[13px] text-gray-500"
                    >
                      <span>{{ getResourceCount(step) }} links</span>
                      <span>|</span>
                      <span>{{ formatHoursToHMS(step.estimated_time) }}</span>
                    </div>
                  </div>
                </div>

                <div v-if="step.completed" class="flex-shrink-0 mt-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      width="20"
                      height="20"
                      rx="10"
                      fill="#32F72F"
                      fill-opacity="0.1"
                    />
                    <rect
                      x="0.25"
                      y="0.25"
                      width="19.5"
                      height="19.5"
                      rx="9.75"
                      stroke="#32F72F"
                      stroke-opacity="0.25"
                      stroke-width="0.5"
                    />
                    <path
                      d="M8.57061 13.4999L5.24561 10.1749L6.07686 9.34369L8.57061 11.8374L13.9227 6.48535L14.7539 7.3166L8.57061 13.4999Z"
                      fill="#2FA81F"
                    />
                  </svg>
                </div>
                <div v-else class="flex-shrink-0 mt-1">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.0003 18.9582C5.05866 18.9582 1.04199 14.9415 1.04199 9.99984C1.04199 5.05817 5.05866 1.0415 10.0003 1.0415C14.942 1.0415 18.9587 5.05817 18.9587 9.99984C18.9587 14.9415 14.942 18.9582 10.0003 18.9582ZM10.0003 2.2915C5.75032 2.2915 2.29199 5.74984 2.29199 9.99984C2.29199 14.2498 5.75032 17.7082 10.0003 17.7082C14.2503 17.7082 17.7087 14.2498 17.7087 9.99984C17.7087 5.74984 14.2503 2.2915 10.0003 2.2915Z"
                      fill="#4C4D4F"
                    />
                    <path
                      d="M7.5002 18.125H6.66686C6.3252 18.125 6.04186 17.8417 6.04186 17.5C6.04186 17.1583 6.30853 16.8833 6.6502 16.875C5.34186 12.4083 5.34186 7.59167 6.6502 3.125C6.30853 3.11667 6.04186 2.84167 6.04186 2.5C6.04186 2.15833 6.3252 1.875 6.66686 1.875H7.5002C7.7002 1.875 7.89186 1.975 8.00853 2.13333C8.1252 2.3 8.15853 2.50833 8.09186 2.7C6.5252 7.40833 6.5252 12.5917 8.09186 17.3083C8.15853 17.5 8.1252 17.7083 8.00853 17.875C7.89186 18.025 7.7002 18.125 7.5002 18.125Z"
                      fill="#4C4D4F"
                    />
                    <path
                      d="M12.5 18.1252C12.4333 18.1252 12.3667 18.1169 12.3 18.0919C11.975 17.9836 11.7917 17.6252 11.9083 17.3002C13.475 12.5919 13.475 7.40857 11.9083 2.6919C11.8 2.3669 11.975 2.00856 12.3 1.90023C12.6333 1.7919 12.9833 1.9669 13.0917 2.2919C14.75 7.25856 14.75 12.7252 13.0917 17.6836C13.0083 17.9586 12.7583 18.1252 12.5 18.1252Z"
                      fill="#4C4D4F"
                    />
                    <path
                      d="M10 14.3335C7.675 14.3335 5.35833 14.0085 3.125 13.3502C3.11667 13.6835 2.84167 13.9585 2.5 13.9585C2.15833 13.9585 1.875 13.6752 1.875 13.3335V12.5002C1.875 12.3002 1.975 12.1085 2.13333 11.9918C2.3 11.8752 2.50833 11.8418 2.7 11.9085C7.40833 13.4752 12.6 13.4752 17.3083 11.9085C17.5 11.8418 17.7083 11.8752 17.875 11.9918C18.0417 12.1085 18.1333 12.3002 18.1333 12.5002V13.3335C18.1333 13.6752 17.85 13.9585 17.5083 13.9585C17.1667 13.9585 16.8917 13.6918 16.8833 13.3502C14.6417 14.0085 12.325 14.3335 10 14.3335Z"
                      fill="#4C4D4F"
                    />
                    <path
                      d="M17.5001 8.12497C17.4335 8.12497 17.3668 8.11663 17.3001 8.09163C12.5918 6.52497 7.40015 6.52497 2.69181 8.09163C2.35848 8.19997 2.00848 8.02497 1.90015 7.69997C1.80015 7.36663 1.97515 7.01663 2.30015 6.9083C7.26681 5.24997 12.7335 5.24997 17.6918 6.9083C18.0168 7.01663 18.2001 7.37497 18.0835 7.69997C18.0085 7.9583 17.7585 8.12497 17.5001 8.12497Z"
                      fill="#4C4D4F"
                    />
                  </svg>
                </div>
              </div>

              <transition name="expand" @enter="onEnter" @leave="onLeave">
                <div
                  v-if="isCardExpanded(step.task_id)"
                  class="space-y-2 mt-4 pt-4 border-t border-border overflow-hidden"
                  @click.stop
                >
                  <div
                    class="flex flex-wrap gap-y-2"
                    style="justify-content: space-between"
                  >
                    <a
                      v-for="(resource, resIndex) in getAllResources(step)"
                      :key="`${step.task_id}-resource-${resIndex}`"
                      :href="resource.url"
                      target="_blank"
                      class="flex items-center space-x-1.5 text-[14px] text-foreground hover:text-primary transition-colors"
                      style="flex: 0 0 calc(33.333% - 0.5rem)"
                    >
                      <img
                        :src="getResourceIcon(resource)"
                        alt="resource platform"
                        class="w-4 h-4"
                      />
                      <span>{{ getResourceLabel(resource, resIndex) }}</span>
                    </a>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-8">
            <p class="text-gray-500 text-[14px]">Loading learning steps...</p>
          </div>
        </div>

        <!-- Comment Section -->
        <div
          v-if="!isProcessing && !loading"
          class="w-full h-[64px] bg-card border-2 border-border rounded-[12px] px-6 flex items-center justify-between"
        >
          <span class="text-muted-foreground text-[14px]"
            >Comment on resources utilised...</span
          >
          <button
            class="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 14H11V9.8L12.6 11.4L14 10L10 6L6 10L7.4 11.4L9 9.8V14ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20Z"
                fill="#4C4D4F"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Side Panel -->
    <transition name="slide-panel">
      <div
        v-if="showSidePanel"
        class="fixed inset-0 z-50"
        @click="closeSidePanel"
      >
        <div
          class="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        ></div>
        <div
          class="absolute right-0 top-0 h-full w-full md:w-[534px] bg-background shadow-2xl border-l border-border overflow-y-auto"
          @click.stop
        >
          <div class="p-6">
            <!-- Header -->
            <div
              class="flex items-center justify-between mb-6 pb-4 border-b border-border"
            >
              <h2 class="text-[18px] font-semibold text-foreground flex-1 pr-4">
                {{ selectedStep?.title }}
              </h2>
              <div class="flex items-center space-x-2">
                <button
                  class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="4" cy="10" r="1.5" fill="#6B7280" />
                    <circle cx="10" cy="10" r="1.5" fill="#6B7280" />
                    <circle cx="16" cy="10" r="1.5" fill="#6B7280" />
                  </svg>
                </button>
                <button
                  @click="closeSidePanel"
                  class="w-8 h-8 flex items-center justify-center rounded-full border transition-colors bg-muted/30 border-border"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 5L5 15M5 5L15 15"
                      stroke="#6B7280"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Status Row -->
            <div class="flex items-center mb-4">
              <div class="flex items-center space-x-3 w-[140px]">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.63379 9.97998C2.34712 12.2733 4.26713 14.04 6.65379 14.5266"
                    stroke="#667085"
                    stroke-width="1.2"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M1.36621 7.32016C1.70621 3.9535 4.54621 1.3335 7.99954 1.3335C11.4529 1.3335 14.2929 3.96016 14.6329 7.32016"
                    stroke="#667085"
                    stroke-width="1.2"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9.33984 14.5332C11.7198 14.0465 13.6332 12.2999 14.3598 10.0132"
                    stroke="#667085"
                    stroke-width="1.2"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                <span class="text-[14px] text-[#6B7280]">Status</span>
              </div>
              <div
                class="flex items-center space-x-2 px-3 py-1 rounded-full"
                :class="
                  selectedStep?.completed
                    ? 'bg-[#22C55E]/10'
                    : 'bg-[#F59E0B]/10'
                "
              >
                <span
                  class="w-2 h-2 rounded-full"
                  :class="
                    selectedStep?.completed ? 'bg-[#22C55E]' : 'bg-[#F59E0B]'
                  "
                ></span>
                <span
                  class="text-[13px] font-medium"
                  :class="
                    selectedStep?.completed
                      ? 'text-[#22C55E]'
                      : 'text-[#F59E0B]'
                  "
                >
                  {{ selectedStep?.completed ? "Completed" : "In Progress" }}
                </span>
              </div>
            </div>

            <!-- Estimated Time Row -->
            <div class="flex items-center mb-6">
              <div class="flex items-center space-x-3 w-[140px]">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.8337 8.83333C13.8337 12.0533 11.2203 14.6667 8.00033 14.6667C4.78033 14.6667 2.16699 12.0533 2.16699 8.83333C2.16699 5.61333 4.78033 3 8.00033 3C11.2203 3 13.8337 5.61333 13.8337 8.83333Z"
                    stroke="#667085"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 5.3335V8.66683"
                    stroke="#667085"
                    stroke-width="1.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6 1.3335H10"
                    stroke="#667085"
                    stroke-width="1.2"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                <span class="text-[14px] text-[#6B7280]">Estimated time</span>
              </div>
              <span class="text-[14px] text-[#9b9c9ead]">{{
                formatHoursToHMS(selectedStep?.estimated_time)
              }}</span>
            </div>

            <!-- Divider -->
            <div class="border-t border-border mb-6"></div>

            <!-- Summary Section -->
            <div class="mb-6">
              <h3 class="text-[14px] font-semibold text-muted-foreground mb-3">
                Summary
              </h3>
              <div class="border border-border rounded-[8px] p-4">
                <p class="text-[14px] text-foreground leading-relaxed">
                  {{
                    selectedStep?.description ||
                    "Learn how CSS controls layout, styling, and visual structure on the web, and understand the foundational concepts used in all modern frontend design."
                  }}
                </p>
              </div>
            </div>

            <!-- Key Points Section -->
            <div
              class="mb-6"
              v-if="
                selectedStep.key_points && selectedStep.key_points.length > 0
              "
            >
              <h3 class="text-[14px] font-semibold text-muted-foreground mb-3">
                Key points
              </h3>
              <div class="border border-border rounded-[8px] p-4">
                <ul class="space-y-2">
                  <li
                    v-for="(point, index) in selectedStep.key_points"
                    :key="index"
                    class="flex items-start space-x-2 text-[14px] text-foreground"
                  >
                    <span class="text-foreground mt-0.5">•</span>
                    <span>{{ point }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Resource Links Section -->
            <div class="mb-6">
              <h3 class="text-[14px] font-semibold text-muted-foreground mb-3">
                Resource Links
              </h3>
              <div class="border border-border rounded-[8px] overflow-hidden">
                <div
                  v-for="(resource, index) in getAllResources(selectedStep)"
                  :key="index"
                  class="flex items-center px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div class="w-[100px] text-[14px] text-foreground">
                    {{ getResourceLabel(resource, index) }}
                  </div>
                  <div class="w-10 flex items-center justify-center">
                    <img
                      :src="getResourceIcon(resource)"
                      alt="resource platform"
                      class="w-5 h-5"
                    />
                  </div>
                  <a
                    :href="resource.url"
                    target="_blank"
                    class="flex-1 text-[14px] text-foreground hover:text-primary transition-colors truncate"
                  >
                    {{ resource.title || "Resource link" }}
                  </a>
                </div>
              </div>
            </div>

            <div class="sticky bottom-0 bg-card pt-4 mt-6">
              <div class="flex items-center justify-between gap-3">
                <button
                  @click="navigateToPreviousTask"
                  :disabled="!hasPreviousTask"
                  class="flex items-center gap-2 px-3 py-2 text-[12px] font-medium text-foreground bg-card border border-border rounded-[8px] hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.5 15L7.5 10L12.5 5"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Previous
                </button>

                <div class="flex items-center gap-2 flex-1">
                  <button
                    @click="openShareModal"
                    class="flex items-center justify-center gap-2 px-3 py-2 text-[12px] font-medium text-foreground bg-card border border-border rounded-[8px] hover:bg-muted transition-colors"
                  >
                    Share
                  </button>

                  <button
                    @click="markCurrentTaskAsComplete"
                    :disabled="selectedStep?.completed"
                    class="flex items-center justify-center gap-2 px-3 py-2 text-[12px] font-medium text-foreground bg-card border border-border rounded-[8px] hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M16.25 5L7.5 13.75L3.75 10"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    Mark as Complete
                  </button>
                </div>

                <button
                  @click="navigateToNextTask"
                  :disabled="!hasNextTask"
                  class="flex items-center gap-2 px-4 py-2 text-[12px] font-medium text-white bg-black dark:bg-white/10 border border-transparent rounded-[8px] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Next
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.5 15L12.5 10L7.5 5"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Share Progress Modal -->
    <transition name="modal-fade">
      <div
        v-if="showShareModal"
        class="fixed inset-0 z-50 flex items-center justify-center"
        @click="closeShareModal"
      >
        <div class="absolute inset-0 bg-black bg-opacity-50"></div>
        <div
          class="relative bg-card rounded-[16px] w-full max-w-[540px] mx-4 shadow-xl border border-border"
          @click.stop
        >
          <!-- Modal Header -->
          <div
            class="flex items-center justify-between p-6 border-b border-gray-200"
          >
            <h2 class="text-[20px] font-semibold text-foreground">
              Share Progress
            </h2>
            <button
              @click="closeShareModal"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <!-- Modal Body -->
          <div class="p-6">
            <p class="text-[14px] text-gray-600 mb-4">
              Share your progress with the community. We've drafted some posts
              for you based on your goal details.
            </p>

            <!-- Post Draft Section -->
            <div class="mb-4">
              <div class="flex items-center justify-between mb-2">
                <label class="text-[14px] font-medium text-gray-700"
                  >Post draft</label
                >
                <button
                  @click="regeneratePostDraft"
                  class="flex items-center gap-2 text-[14px] text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.65 2.35C12.2 0.9 10.21 0 8 0C3.58 0 0.01 3.58 0.01 8C0.01 12.42 3.58 16 8 16C11.73 16 14.84 13.45 15.73 10H13.65C12.83 12.33 10.61 14 8 14C4.69 14 2 11.31 2 8C2 4.69 4.69 2 8 2C9.66 2 11.14 2.69 12.22 3.78L9 7H16V0L13.65 2.35Z"
                      fill="currentColor"
                    />
                  </svg>
                  Regenerate with AI
                </button>
              </div>
              <div class="bg-muted/30 border border-border rounded-[8px] p-4">
                <p class="text-[14px] text-gray-900 mb-3 whitespace-pre-wrap">
                  {{ sharePostDraft }}
                </p>
                <p class="text-[12px] text-gray-500">
                  {{ shareCharCount }} chars
                </p>
              </div>
            </div>

            <!-- Goal Link -->
            <div
              class="flex items-center gap-3 bg-muted rounded-[8px] p-3 mb-4"
            >
              <div
                class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-[16px] font-semibold text-gray-700"
              >
                {{ currentUser.first_name?.charAt(0) || "U"
                }}{{ currentUser.last_name?.charAt(0) || "" }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-[14px] font-medium text-gray-900 truncate">
                  {{ learningGoal?.topic }}
                </p>
                <p class="text-[12px] text-gray-500 truncate">
                  {{ shareGoalUrl }}
                </p>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex items-center gap-3">
              <button
                @click="copyPostText"
                class="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-foreground bg-card border border-border rounded-[8px] hover:bg-muted transition-colors"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13.333 10.75V14.083C13.333 15.683 12.1247 16.9163 10.583 16.9163H5.91634C4.37467 16.9163 3.16634 15.683 3.16634 14.083V9.41634C3.16634 7.81634 4.37467 6.58301 5.91634 6.58301H9.24967"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M13.3337 10.75H11.0003C9.58366 10.75 8.83366 10 8.83366 8.58333V6.25L13.3337 10.75Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M9.66699 3.08301H12.167"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M6.66699 5.41699C6.66699 4.26699 7.60033 3.33366 8.75033 3.33366H10.2587"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M16.667 6.66699V11.2253C16.667 12.292 15.8003 13.167 14.7337 13.167"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M16.667 6.66699H14.167C12.8337 6.66699 12.167 6.00033 12.167 4.66699V2.16699L16.667 6.66699Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                Copy Text
              </button>

              <div class="flex items-center gap-2 ml-auto">
                <span class="text-[14px] text-gray-600">or Share on</span>
                <a
                  :href="getInstagramShareUrl()"
                  target="_blank"
                  class="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:opacity-90 transition-opacity"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 1.8C12.67 1.8 12.99 1.81 14.04 1.86C15.01 1.9 15.54 2.07 15.9 2.21C16.37 2.4 16.71 2.62 17.07 2.98C17.43 3.34 17.65 3.68 17.84 4.15C17.98 4.51 18.15 5.04 18.19 6.01C18.24 7.06 18.25 7.38 18.25 10.05C18.25 12.72 18.24 13.04 18.19 14.09C18.15 15.06 17.98 15.59 17.84 15.95C17.65 16.42 17.43 16.76 17.07 17.12C16.71 17.48 16.37 17.7 15.9 17.89C15.54 18.03 15.01 18.2 14.04 18.24C12.99 18.29 12.67 18.3 10 18.3C7.33 18.3 7.01 18.29 5.96 18.24C4.99 18.2 4.46 18.03 4.1 17.89C3.63 17.7 3.29 17.48 2.93 17.12C2.57 16.76 2.35 16.42 2.16 15.95C2.02 15.59 1.85 15.06 1.81 14.09C1.76 13.04 1.75 12.72 1.75 10.05C1.75 7.38 1.76 7.06 1.81 6.01C1.85 5.04 2.02 4.51 2.16 4.15C2.35 3.68 2.57 3.34 2.93 2.98C3.29 2.62 3.63 2.4 4.1 2.21C4.46 2.07 4.99 1.9 5.96 1.86C7.01 1.81 7.33 1.8 10 1.8ZM10 0C7.28 0 6.94 0.01 5.88 0.06C4.82 0.11 4.08 0.28 3.45 0.53C2.79 0.79 2.23 1.13 1.68 1.68C1.13 2.23 0.79 2.79 0.53 3.45C0.28 4.08 0.11 4.82 0.06 5.88C0.01 6.94 0 7.28 0 10C0 12.72 0.01 13.06 0.06 14.12C0.11 15.18 0.28 15.92 0.53 16.55C0.79 17.21 1.13 17.77 1.68 18.32C2.23 18.87 2.79 19.21 3.45 19.47C4.08 19.72 4.82 19.89 5.88 19.94C6.94 19.99 7.28 20 10 20C12.72 20 13.06 19.99 14.12 19.94C15.18 19.89 15.92 19.72 16.55 19.47C17.21 19.21 17.77 18.87 18.32 18.32C18.87 17.77 19.21 17.21 19.47 16.55C19.72 15.92 19.89 15.18 19.94 14.12C19.99 13.06 20 12.72 20 10C20 7.28 19.99 6.94 19.94 5.88C19.89 4.82 19.72 4.08 19.47 3.45C19.21 2.79 18.87 2.23 18.32 1.68C17.77 1.13 17.21 0.79 16.55 0.53C15.92 0.28 15.18 0.11 14.12 0.06C13.06 0.01 12.72 0 10 0Z"
                    />
                    <path
                      d="M10 4.87C7.16 4.87 4.87 7.16 4.87 10C4.87 12.84 7.16 15.13 10 15.13C12.84 15.13 15.13 12.84 15.13 10C15.13 7.16 12.84 4.87 10 4.87ZM10 13.33C8.16 13.33 6.67 11.84 6.67 10C6.67 8.16 8.16 6.67 10 6.67C11.84 6.67 13.33 8.16 13.33 10C13.33 11.84 11.84 13.33 10 13.33Z"
                    />
                    <circle cx="15.34" cy="4.66" r="1.2" />
                  </svg>
                </a>
                <a
                  :href="getFacebookShareUrl()"
                  target="_blank"
                  class="w-10 h-10 flex items-center justify-center rounded-full bg-[#1877F2] hover:opacity-90 transition-opacity"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 10C20 4.48 15.52 0 10 0C4.48 0 0 4.48 0 10C0 14.84 3.44 18.87 8 19.8V13H6V10H8V7.5C8 5.57 9.57 4 11.5 4H14V7H12C11.45 7 11 7.45 11 8V10H14V13H11V19.95C16.05 19.45 20 15.19 20 10Z"
                    />
                  </svg>
                </a>
                <a
                  :href="getLinkedInShareUrl()"
                  target="_blank"
                  class="w-10 h-10 flex items-center justify-center rounded-full bg-[#0A66C2] hover:opacity-90 transition-opacity"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18.52 0H1.48C0.66 0 0 0.64 0 1.44V18.56C0 19.36 0.66 20 1.48 20H18.52C19.34 20 20 19.36 20 18.56V1.44C20 0.64 19.34 0 18.52 0ZM6 17H3V7.5H6V17ZM4.5 6.2C3.53 6.2 2.75 5.42 2.75 4.45C2.75 3.48 3.53 2.7 4.5 2.7C5.47 2.7 6.25 3.48 6.25 4.45C6.25 5.42 5.47 6.2 4.5 6.2ZM17 17H14V12.4C14 11.23 14 9.73 12.38 9.73C10.76 9.73 10.5 11 10.5 12.32V17H7.5V7.5H10.38V8.88H10.42C10.84 8.09 11.87 7.27 13.42 7.27C16.46 7.27 17 9.23 17 11.77V17Z"
                    />
                  </svg>
                </a>
                <a
                  :href="getWhatsAppShareUrl()"
                  target="_blank"
                  class="w-10 h-10 flex items-center justify-center rounded-full bg-[#25D366] hover:opacity-90 transition-opacity"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="white"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.05 0C4.5 0 0 4.48 0 10C0 11.89 0.53 13.66 1.44 15.16L0.5 19.5L5.05 18.58C6.49 19.38 8.17 19.85 10 19.85C15.5 19.85 20 15.37 20 9.85C20 7.19 18.95 4.69 17.07 2.81C15.19 0.93 12.69 0 10.05 0ZM10.05 1.82C12.23 1.82 14.25 2.66 15.77 4.18C17.29 5.7 18.18 7.72 18.18 9.85C18.18 14.37 14.5 18.03 10 18.03C8.45 18.03 6.95 17.61 5.66 16.84L5.31 16.63L2.32 17.41L3.12 14.54L2.88 14.16C2.03 12.81 1.58 11.25 1.58 9.62C1.58 5.1 5.26 1.44 9.76 1.44L10.05 1.82ZM6.65 5.28C6.49 5.28 6.21 5.34 5.98 5.58C5.75 5.82 5.09 6.44 5.09 7.7C5.09 8.96 6 10.18 6.13 10.35C6.26 10.52 7.93 13.17 10.54 14.25C12.73 15.17 13.15 15 13.61 14.96C14.07 14.92 15.11 14.34 15.33 13.73C15.55 13.12 15.55 12.6 15.49 12.49C15.43 12.38 15.26 12.32 15 12.19C14.74 12.06 13.48 11.44 13.25 11.36C13.02 11.28 12.85 11.24 12.68 11.5C12.51 11.76 12.03 12.32 11.88 12.49C11.73 12.66 11.58 12.68 11.32 12.55C11.06 12.42 10.22 12.15 9.23 11.27C8.46 10.59 7.93 9.75 7.78 9.49C7.63 9.23 7.77 9.08 7.9 8.95C8.01 8.84 8.16 8.65 8.29 8.5C8.42 8.35 8.46 8.24 8.54 8.07C8.62 7.9 8.58 7.75 8.51 7.62C8.44 7.49 7.92 6.23 7.71 5.71C7.5 5.19 7.29 5.26 7.14 5.25C6.99 5.24 6.82 5.24 6.65 5.24V5.28Z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import apiClient from "@/axios";
import LearningGoalActionsBar from "./LearningGoalActionsBar.vue";
import youtubeIcon from "@/assets/platforms/youtube.svg";
import courseraIcon from "@/assets/platforms/coursera.svg";
import skillshareIcon from "@/assets/platforms/skillshare.svg";
import udemyIcon from "@/assets/platforms/udemy.svg";
import Loader from "../ui/Loader.vue";

export default {
  name: "LearningGoalTaskDetailComponent",
  components: {
    LearningGoalActionsBar,
    Loader,
  },
  data() {
    return {
      learningGoal: {},
      learningSteps: [],
      parentTaskInfo: null,
      statistics: {},
      loading: true,
      showSuccess: false,
      successMessage: "",
      showError: false,
      errorMessage: "",
      expandedCards: {},
      selectedStep: null,
      showSidePanel: false,
      showShareModal: false,
      descriptionExpanded: false,
      defaultDescription: "View details for your selected learning goal.",
      icons: {
        youtube: youtubeIcon,
        coursera: courseraIcon,
        udemy: udemyIcon,
        skillshare: skillshareIcon,
      },
    };
  },
  computed: {
    projectId() {
      return this.$route.params.id;
    },
    taskId() {
      return this.$route.params.taskId;
    },
    currentUser() {
      return this.$store.state.user || {};
    },

    badgeEarned() {
      if (
        this.statistics.total_tasks === this.statistics.completed_tasks &&
        this.statistics.total_tasks > 0
      ) {
        return `${this.learningGoal.topic} Badge`;
      }
      return "";
    },

    isProcessing() {
      return this.learningGoal.status?.toLowerCase() === "processing";
    },

    currentTaskIndex() {
      return this.learningSteps.findIndex(
        (step) => step.task_id === this.selectedStep?.task_id,
      );
    },

    hasPreviousTask() {
      return this.currentTaskIndex > 0;
    },

    hasNextTask() {
      return (
        this.currentTaskIndex >= 0 &&
        this.currentTaskIndex < this.learningSteps.length - 1
      );
    },

    sharePostDraft() {
      const stepTitle = this.selectedStep?.title || "this module";
      const goalTopic = this.learningGoal?.topic || "my learning goal";
      return `One step closer to my goal: ${goalTopic}!\n\nI learnt the following in this module:\n• ${stepTitle}\n\nLearn more about this goal here: ${this.shareGoalUrl}`;
    },

    shareCharCount() {
      return this.sharePostDraft.length;
    },

    shareGoalUrl() {
      return `${window.location.origin}/learning-goals/${this.projectId}`;
    },
  },
  methods: {
    async fetchLearningGoalDetails() {
      try {
        this.loading = true;

        const goalResponse = await apiClient.get(
          `/web/learning-goals/${this.projectId}`,
        );
        this.learningGoal = goalResponse.data || {};

        const response = await apiClient.get(
          `/web/learning-goals/${this.projectId}/tasks/${this.taskId}/sub-tasks`,
        );
        const data = response.data || [];

        if (data.length > 0 && data[0].parent_task_info) {
          this.parentTaskInfo = data[0].parent_task_info;
        }

        const steps = [];

        const mapItemToStep = (item) => {
          const external_resources = this.processResources(
            item.external_resources || [],
          );
          return {
            task_id: item.task_id || item.id,
            title: item.title,
            description: item.description,
            completed: item.status?.toLowerCase() === "completed",
            inProgress: item.status?.toLowerCase() === "inprogress",
            status: item.status,
            learning_task_type: item.learning_task_type,
            priority: item.priority,
            external_resources: external_resources,
            assessment_criteria: item.assessment_criteria || {},
            key_points: item.key_takeaways || [],
            start_date: item.start_date,
            due_date: item.due_date,
            estimated_time: external_resources.reduce(
              (sum, res) => sum + (res.estimated_hours || 0),
              0,
            ),
          };
        };

        data.forEach((subTask) => {
          const subStep = mapItemToStep(subTask);
          if (this.shouldIncludeStep(subStep)) {
            steps.push(subStep);
          }
        });

        this.learningSteps = steps;
      } catch (error) {
        console.error("Error fetching learning goal sub-tasks:", error);
        this.handleError(
          "Failed to load learning goal sub-tasks. Please try again.",
        );
      } finally {
        this.loading = false;
      }
    },

    processResources(resources) {
      return resources.map((resource) => ({
        title: resource.title || resource.name || "Untitled Resource",
        url: resource.url || resource.video_url || resource.pdf_url || "#",
        type: this.determineResourceType(resource),
        completed: resource.completed || false,
        estimated_hours: resource.estimated_hours || 0,
      }));
    },

    normalizeTaskType(type) {
      const t = (type || "").toString().trim().toLowerCase();
      if (!t) return "";
      if (t === "assessment" || t === "assesment") return "assessment";
      if (t === "lesson") return "lesson";
      return t;
    },

    isAssessment(step) {
      return this.normalizeTaskType(step.learning_task_type) === "assessment";
    },

    shouldIncludeStep(step) {
      const t = this.normalizeTaskType(step.learning_task_type);
      const hasResources = (step.external_resources?.length || 0) > 0;
      // Exclude assessment tasks from the list
      if (t === "assessment") return false;
      if (t === "lesson") return hasResources;
      return hasResources;
    },

    getResourcePlatform(resource) {
      const url = (resource.url || resource.link || "").toLowerCase();
      if (
        url.includes("youtu.be") ||
        url.includes("youtube.com") ||
        url.includes("youtube")
      )
        return "youtube";
      if (url.includes("udemy.com") || url.includes("udemy")) return "udemy";
      if (url.includes("coursera.org") || url.includes("coursera"))
        return "coursera";
      if (url.includes("skillshare.com") || url.includes("skillshare"))
        return "skillshare";
      return "default";
    },

    getResourceLabel(resource, index) {
      const type = resource.type || this.determineResourceType(resource);
      const number = index + 1;

      if (type === "video") {
        return `Watch ${number}`;
      } else if (type === "document" || type === "link") {
        return `Read ${number}`;
      }
      return `Link ${number}`;
    },

    getResourceIcon(resource) {
      const platform = this.getResourcePlatform(resource);
      switch (platform) {
        case "youtube":
          return this.icons.youtube;
        case "udemy":
          return this.icons.udemy;
        case "skillshare":
          return this.icons.skillshare;
        case "coursera":
        default:
          return this.icons.coursera;
      }
    },

    determineResourceType(resource) {
      const url = (resource.url || resource.link || "").toLowerCase();
      const title = (resource.title || resource.name || "").toLowerCase();

      if (
        url.includes("youtube.com") ||
        url.includes("youtu.be") ||
        url.includes("youtube") ||
        title.includes("video")
      ) {
        return "video";
      }
      if (
        url.includes(".pdf") ||
        title.includes("document") ||
        title.includes("article") ||
        title.includes("read")
      ) {
        return "document";
      }
      if (
        title.includes("tutorial") ||
        title.includes("interactive") ||
        title.includes("practice")
      ) {
        return "tutorial";
      }
      return "link";
    },

    getAllResources(step) {
      return step?.external_resources || [];
    },

    getResourceCount(step) {
      return step?.external_resources?.length || 0;
    },

    formatHoursToHMS(hours) {
      const num = Number(hours);
      if (!isFinite(num) || num < 0) return "n/a";
      const totalSeconds = Math.round(num * 3600);
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      const parts = [];
      if (h > 0) parts.push(`${h}h`);
      if (m > 0) parts.push(`${m}m`);
      if (s > 0 || parts.length === 0) parts.push(`${s}s`);
      return parts.join(" ");
    },

    toggleStepCompletion(step) {
      step.completed = !step.completed;
      // TODO: Call API to update step completion status
      console.log("Toggle step completion:", step.task_id, step.completed);
    },

    toggleResourceCompletion(step, resource) {
      resource.completed = !resource.completed;
      // TODO: Call API to update resource completion status
      console.log(
        "Toggle resource completion:",
        resource.title,
        resource.completed,
      );
    },

    toggleCardExpansion(taskId) {
      this.expandedCards[taskId] = !this.expandedCards[taskId];
    },

    handleCardClick(index, step) {
      if (!this.isCardActive(index)) {
        this.$toast.warning(
          "Locked",
          "Complete previous topics to unlock this one.",
        );
        return;
      }
      this.toggleCardExpansion(step.task_id);
    },

    isCardExpanded(taskId) {
      return !!this.expandedCards[taskId];
    },

    isCardActive(index) {
      if (index === 0) return true;
      const previousStep = this.learningSteps[index - 1];
      return previousStep && previousStep.completed;
    },

    onEnter(el) {
      el.style.height = "0";
      el.style.opacity = "0";
      el.offsetHeight; // trigger reflow
      el.style.transition = "height 0.3s ease, opacity 0.3s ease";
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
    },

    onLeave(el) {
      el.style.height = el.scrollHeight + "px";
      el.style.opacity = "1";
      el.offsetHeight; // trigger reflow
      el.style.transition = "height 0.3s ease, opacity 0.3s ease";
      el.style.height = "0";
      el.style.opacity = "0";
    },

    openSidePanel(step) {
      this.selectedStep = step;
      this.showSidePanel = true;
    },

    closeSidePanel() {
      this.showSidePanel = false;
      setTimeout(() => {
        this.selectedStep = null;
      }, 300);
    },

    getStepStatusText(step) {
      if (step.completed) return "Completed";
      if (step.inProgress) return "In Progress";
      return "Not Started";
    },

    getStepStatusColor(step) {
      if (step.completed) {
        return {
          text: "#22C55E",
          bg: "#F0FDF4",
          border: "#22C55E",
        };
      }
      if (step.inProgress) {
        return {
          text: "#F59E0B",
          bg: "#FEF3C7",
          border: "#F59E0B",
        };
      }
      return {
        text: "#6B7280",
        bg: "#F9FAFB",
        border: "#D1D5DB",
      };
    },

    truncateDescription(description) {
      if (!description) return "";
      const maxLength = 100;
      if (description.length <= maxLength) return description;
      return description.substring(0, maxLength).trim() + "...";
    },

    toggleDescription() {
      this.descriptionExpanded = !this.descriptionExpanded;
    },

    onBookMentor(response) {
      console.log("Mentor session booked:", response);
      if (response && response.message) {
        this.$toast.success("Session Booked", response.message);
      }
    },

    onEditGoal() {
      this.$router.push({
        name: "LearningGoalDetails",
        params: { id: this.projectId },
        query: { edit: "true" },
      });
    },

    trackProgress() {
      this.$router.push({
        name: "ProjectDetails",
        params: { projectId: this.projectId },
      });
    },

    handleError(message) {
      this.showSuccess = false;
      this.successMessage = "";
      this.showError = true;
      this.errorMessage = message;

      setTimeout(() => {
        this.showError = false;
        this.errorMessage = "";
      }, 5000);
    },

    handleSuccess(message) {
      this.showError = false;
      this.errorMessage = "";
      this.showSuccess = true;
      this.successMessage = message;

      setTimeout(() => {
        this.showSuccess = false;
        this.successMessage = "";
      }, 5000);
    },

    navigateToPreviousTask() {
      if (this.hasPreviousTask) {
        const previousStep = this.learningSteps[this.currentTaskIndex - 1];
        this.selectedStep = previousStep;
      }
    },

    navigateToNextTask() {
      if (this.hasNextTask) {
        const nextStep = this.learningSteps[this.currentTaskIndex + 1];
        this.selectedStep = nextStep;
      }
    },

    async markCurrentTaskAsComplete() {
      if (!this.selectedStep) return;

      this.showSuccess = false;
      this.successMessage = "";
      this.showError = false;
      this.errorMessage = "";

      try {
        const payload = {
          task_status: "Completed",
        };

        await apiClient.put(
          `/web/tasks/${this.selectedStep.task_id}?project_id=${this.projectId}`,
          payload,
        );

        this.showSuccess = true;
        this.successMessage =
          "You have successfully completed this task. Well done!";

        this.selectedStep.completed = true;
        this.selectedStep.status = "Completed";

        await this.fetchLearningGoalDetails();
      } catch (error) {
        this.showError = true;

        if (error.response && error.response.status === 403) {
          this.errorMessage =
            "You are not allowed to mark this task as completed. Only the task creator can complete this task.";
        } else if (error.response && error.response.status === 401) {
          this.errorMessage =
            "You are not authorized to perform this action. Please log in again.";
        } else if (error.response && error.response.status === 404) {
          this.errorMessage = "Task not found. It may have been deleted.";
        } else if (error.response && error.response.status >= 500) {
          this.errorMessage = "Server error occurred. Please try again later.";
        } else {
          this.errorMessage =
            "An error occurred while updating the task. Please try again.";
        }
      }
    },

    scrollToAndOpenTask(taskId) {
      const step = this.learningSteps.find((s) => s.task_id == taskId);
      if (step) {
        this.openSidePanel(step);
      }
    },

    openShareModal() {
      this.showShareModal = true;
    },

    closeShareModal() {
      this.showShareModal = false;
    },

    async regeneratePostDraft() {
      console.log("Regenerate post draft clicked");
      // this.handleSuccess("Post regenerated!");
    },

    async copyPostText() {
      try {
        await navigator.clipboard.writeText(this.sharePostDraft);
        this.handleSuccess("Post copied to clipboard!");
      } catch (error) {
        this.handleError("Failed to copy to clipboard. Please try again.");
      }
    },

    getInstagramShareUrl() {
      //NOTE: Instagram doesn't support URL sharing directly
      // return "https://www.instagram.com/";
    },

    getFacebookShareUrl() {
      const url = encodeURIComponent(this.shareGoalUrl);
      const quote = encodeURIComponent(this.sharePostDraft);
      return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`;
    },

    getLinkedInShareUrl() {
      const url = encodeURIComponent(this.shareGoalUrl);
      return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    },

    getWhatsAppShareUrl() {
      const text = encodeURIComponent(
        `${this.sharePostDraft}\n\n${this.shareGoalUrl}`,
      );
      return `https://wa.me/?text=${text}`;
    },
  },

  created() {
    this.fetchLearningGoalDetails();
    this.$toast.success(
      "Session Booked",
      "Test message for booking mentor session.",
    );
  },

  watch: {
    learningSteps: {
      handler(steps) {
        if (steps && steps.length > 0) {
          const activeIndex = steps.findIndex((step) => !step.completed);
          if (activeIndex !== -1) {
            this.expandedCards[steps[activeIndex].task_id] = true;
          }
        }
      },
      immediate: false,
    },
  },
};
</script>

<style scoped>
/* Card hover effects */
.bg-white:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s ease;
}

/* Resource item hover */
.hover\:bg-gray-50:hover {
  background-color: #f9fafb;
}

/* Checkbox animation */
.w-5.h-5.rounded {
  transition: all 0.2s ease;
}

/* Link hover color transition */
a {
  transition: color 0.2s ease;
}

/* Smooth transitions for status badges */
span {
  transition: all 0.2s ease;
}

/* Processing animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Expand/Collapse animation */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 500px;
}

/* Side Panel Animation */
.slide-panel-enter-active,
.slide-panel-leave-active {
  transition: opacity 0.3s ease;
}

.slide-panel-enter-active .absolute.right-0,
.slide-panel-leave-active .absolute.right-0 {
  transition: transform 0.3s ease;
}

.slide-panel-enter-from,
.slide-panel-leave-to {
  opacity: 0;
}

.slide-panel-enter-from .absolute.right-0 {
  transform: translateX(100%);
}

.slide-panel-leave-to .absolute.right-0 {
  transform: translateX(100%);
}

.slide-panel-enter-to .absolute.right-0,
.slide-panel-leave-from .absolute.right-0 {
  transform: translateX(0);
}

/* Modal Fade Animation */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-to,
.modal-fade-leave-from {
  opacity: 1;
}
</style>
